import React, { useMemo } from "react";
import styled from "styled-components";
import { MOTIFS } from "./motifData";

/* ─── Which groups are missing from a keyset ─────────────── */
const getMissing = (keys) => {
  const has = (k) => keys.has("single-regulator:" + k);
  const missing = [];
  if (!has("basic-positive") && !has("inverted-positive") && !has("basic-negative") && !has("inverted-negative"))
    missing.push("Regulator type");
  if (!has("strength-regular") && !has("strength-weak"))
    missing.push("Regulation strength");
  if (!has("weight-none") && !has("weight-yes"))
    missing.push("Regulation weight");
  if (!has("memory-without") && !has("memory-with"))
    missing.push("Memory");
  if (!has("spont-none") && !has("spont-yes") && !has("spont-increase") && !has("spont-decrease"))
    missing.push("Spontaneous behavior");
  if (!has("increment-prop") && !has("increment-zero") && !has("increment-other"))
    missing.push("Increment");
  if (!has("levels-2") && !has("levels-3") && !has("levels-4") && !has("levels-5"))
    missing.push("Number of levels");
  return missing;
};

const ALL_LEVELS = [2, 3, 4, 5];

/* ─── Match a keysSet to a MOTIF entry ───────────────────── */
// ALL six groups must have a selection — otherwise returns null.
const matchMotif = (keys) => {
  const has = (k) => keys.has("single-regulator:" + k);
  const type       = has("basic-positive")    ? "basic-positive"
                   : has("inverted-positive") ? "inverted-positive"
                   : has("basic-negative")    ? "basic-negative"
                   : has("inverted-negative") ? "inverted-negative"
                   : null;
  const strength   = has("strength-regular") ? "strength-regular"
                   : has("strength-weak")    ? "strength-weak" : null;
  const weight     = has("weight-none") ? "weight-none"
                   : has("weight-yes")  ? "weight-yes" : null;
  const memory     = has("memory-without") ? "memory-without"
                   : has("memory-with")    ? "memory-with" : null;
  const spontaneous = has("spont-none")     ? "spont-none"
                    : has("spont-yes")      ? "spont-yes"
                    : has("spont-increase") ? "spont-increase"
                    : has("spont-decrease") ? "spont-decrease" : null;
  const increment  = has("increment-prop")  ? "increment-prop"
                   : has("increment-zero")  ? "increment-zero"
                   : has("increment-other") ? "increment-other" : null;

  // Every group must be selected
  if (!type || !strength || !weight || !memory || !spontaneous || !increment) return null;

  return MOTIFS.find(m =>
    m.type        === type        &&
    m.strength    === strength    &&
    m.weight      === weight      &&
    m.memory      === memory      &&
    m.spontaneous === spontaneous &&
    m.increment   === increment
  ) ?? null;
};

const getLevels = (keys) => {
  const sel = ALL_LEVELS.filter(l => keys.has(`single-regulator:levels-${l}`));
  return sel.length > 0 ? sel : null;
};

/* ─── Styled components ──────────────────────────────────── */
const Wrap = styled.div`
  width: 100%;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  align-items: stretch;
  width: 100%;
`;

const Card = styled.div`
  width: 100%;
  flex: 1;
  border: 1.5px solid #99b3d4;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 10px rgba(176,96,160,0.08);
  display: flex;
  flex-direction: column;
`;

const LiveCard = styled(Card)`
  border: 2px solid #FFB81C;
  box-shadow: 0 0 0 3px rgba(255,184,28,0.25), 0 2px 14px rgba(0,53,148,0.15);
  background: #fffbef;
`;

const NewCard = styled(Card)`
  animation: borderFlash 1.6s ease-out forwards;

  @keyframes borderFlash {
    0%   { border-color: #FFB81C; box-shadow: 0 0 0 4px rgba(255,184,28,0.6); }
    40%  { border-color: #FFB81C; box-shadow: 0 0 0 8px rgba(255,184,28,0.3); }
    100% { border-color: #99b3d4; box-shadow: 0 2px 10px rgba(176,96,160,0.08); }
  }
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #003594;
  border-radius: 10px;
  padding: 2px 8px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const LiveDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.85;
`;

const CardHeader = styled.div`
  background: #f0f0f0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1.5px solid #99b3d4;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #00205B;
  line-height: 1.3;
  flex: 1;
`;

/* Tiny B→A diagram */
const DiagramSvg = styled.svg`
  flex-shrink: 0;
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const InfoKey = styled.td`
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #00205B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: top;
  white-space: nowrap;
  width: 110px;
  border-bottom: 1px solid #DBEEFF;
  background: #EEF3FF;
`;

const InfoVal = styled.td`
  padding: 8px 12px;
  color: #111;
  font-size: 13px;
  vertical-align: top;
  border-bottom: 1px solid #DBEEFF;
  line-height: 1.5;
`;

const DescVal = styled(InfoVal)`
  color: #003594;
  font-style: italic;
  font-size: 13px;
`;

const DishVal = styled(InfoVal)`
  font-family: "SFMono-Regular", "Consolas", monospace;
  font-size: 12px;
  color: #222;
`;

const BioVal = styled(InfoVal)`
  white-space: pre-line;
  color: #444;
  font-size: 12px;
`;

const BiorecipeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const BiorecipeLine = styled.div`
  font-size: 12px;
  line-height: 1.5;
`;

const Bold = styled.span`
  font-weight: 700;
  color: #00205B;
`;

/* ── Plot table ── */
const PlotSection = styled.div`
  border-top: 1.5px solid #99b3d4;
  background: #f5f8ff;
  overflow-x: auto;
`;

const PlotSectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #003594;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 10px 4px;
`;

const PlotTableWrap = styled.div`
  width: ${({ $levels }) => $levels === 1 ? '50%' : $levels === 2 ? '75%' : '100%'};
  min-width: ${({ $levels }) => $levels * 120}px;
`;

const PlotTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
`;

const ThCorner = styled.th`
  width: 36px;
  background: #DBEEFF;
  border: 1px solid #99b3d4;
  padding: 4px;
`;

const ThLevel = styled.th`
  background: #DBEEFF;
  border: 1px solid #99b3d4;
  padding: 4px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #00205B;
  text-align: center;
  white-space: nowrap;
`;

const TdRow = styled.td`
  background: #EEF3FF;
  border: 1px solid #99b3d4;
  padding: 4px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #00205B;
  text-align: center;
  white-space: nowrap;
`;

const TdCell = styled.td`
  border: 1px solid #ccd9ef;
  padding: 3px;
  text-align: center;
  background: #fff;
  min-width: 100px;
`;

const PlotImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
`;

const ImgPlaceholder = styled.div`
  width: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: #ddd;
  margin: 0 auto;
  border: 1px dashed #eee;
  border-radius: 2px;
`;

const B_VALUES = [0, 1, 2, 3, 4];
const imgPath = (motifId, level, b) =>
  `/images/computational/${motifId}_L${level}_B${b}.png`;

const PlotCell = ({ motifId, level, b }) => {
  const [errored, setErrored] = React.useState(false);
  const src = imgPath(motifId, level, b);
  React.useEffect(() => { setErrored(false); }, [src]);
  if (errored) return <ImgPlaceholder title={src}>no img</ImgPlaceholder>;
  return (
    <PlotImg src={src} alt={`L${level} B${b}`} title={src} onError={() => setErrored(true)} />
  );
};

const EmptyCard = styled.div`
  flex-shrink: 0;
  width: 260px;
  min-height: 200px;
  border: 1.5px dashed #99b3d4;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background: #fff;
`;

const EmptyText = styled.p`
  font-size: 11px;
  color: #6699cc;
  font-style: italic;
  text-align: center;
  line-height: 1.6;
  margin: 0;
`;



/* ─── Mini B→A diagram ───────────────────────────────────── */
const MotifDiagram = ({ type }) => {
  const isNeg = type === "basic-negative" || type === "inverted-negative";
  const color = isNeg ? "#FF5B45" : "#66B2E3";
  return (
    <DiagramSvg width="70" height="36" viewBox="0 0 70 36">
      <ellipse cx="12" cy="18" rx="10" ry="9" fill="#fff" stroke="#770538" strokeWidth="1.5" />
      <text x="12" y="22" textAnchor="middle" fontSize="8" fontWeight="700" fill="#770538">B</text>
      <ellipse cx="58" cy="18" rx="10" ry="9" fill="#fff" stroke="#B87333" strokeWidth="1.5" />
      <text x="58" y="22" textAnchor="middle" fontSize="8" fontWeight="700" fill="#B87333">A</text>
      <defs>
        <marker id={`arr-${type}`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          {isNeg
            ? <line x1="1" y1="1" x2="1" y2="5" stroke={color} strokeWidth="1.5" />
            : <polygon points="0,0 6,3 0,6" fill={color} />}
        </marker>
      </defs>
      <line x1="23" y1="18" x2="42" y2="18" stroke={color} strokeWidth="1.4"
        markerEnd={`url(#arr-${type})`}
        strokeDasharray={isNeg ? "none" : "none"} />
    </DiagramSvg>
  );
};

/* ─── Single info card ───────────────────────────────────── */
const MissingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
`;

const MissingItem = styled.div`
  font-size: 12px;
  color: #003594;
  padding: 4px 14px;
  background: #DBEEFF;
  border-radius: 4px;
  font-weight: 500;
`;

const MissingHint = styled.div`
  font-size: 13px;
  color: #888;
  font-style: italic;
  text-align: center;
  margin-bottom: 12px;
`;

const IncompleteBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  gap: 0;
`;

const InfoToggleBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: #DBEEFF;
  border: none;
  border-bottom: ${({ $open }) => $open ? "1px solid #99b3d4" : "none"};
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  color: #00205B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: background 0.12s;

  &:hover { background: #99b3d4; }
`;

const ChevronIcon = styled.svg`
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => $open ? "180deg" : "0deg"});
  flex-shrink: 0;
`;

const InfoCard = ({ keys, isLive = false, isNew = false }) => {
  const [infoOpen, setInfoOpen] = React.useState(isLive);
  const motif = matchMotif(keys);
  const levels = getLevels(keys);
  const CardEl = isLive ? LiveCard : isNew ? NewCard : Card;

  if (!motif || !levels) {
    const missing = getMissing(keys);
    return (
      <CardEl>
        <CardHeader>
          <CardTitle style={{ color: "#aaa", fontStyle: "italic" }}>Incomplete selection</CardTitle>
        </CardHeader>
        <IncompleteBody>
          <MissingHint>Select one option from each group to see the motif:</MissingHint>
          <MissingList>
            {missing.map(m => <MissingItem key={m}>{m}</MissingItem>)}
          </MissingList>
        </IncompleteBody>
      </CardEl>
    );
  }

  const { card } = motif;
  return (
    <CardEl>
      <CardHeader>
        <MotifDiagram type={motif.type} />
        <CardTitle>{motif.label}</CardTitle>
      </CardHeader>

      {/* ── Collapsible info toggle ── */}
      <InfoToggleBtn $open={infoOpen} onClick={() => setInfoOpen(o => !o)}>
        <span>Information</span>
        <ChevronIcon $open={infoOpen} width="10" height="10" viewBox="0 0 10 10">
          <polyline points="2,3 5,7 8,3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </ChevronIcon>
      </InfoToggleBtn>

      {infoOpen && (
        <InfoTable>
          <tbody>
            <tr>
              <InfoKey>Description</InfoKey>
              <DescVal>{card.description}</DescVal>
            </tr>
            <tr>
              <InfoKey>Memory</InfoKey>
              <InfoVal>{card.memoryVal}</InfoVal>
            </tr>
            <tr>
              <InfoKey>Strength</InfoKey>
              <InfoVal>{card.strengthVal}</InfoVal>
            </tr>
            <tr>
              <InfoKey>Weight</InfoKey>
              <InfoVal>{card.weightVal}</InfoVal>
            </tr>
            <tr>
              <InfoKey>BioRECIPE</InfoKey>
              <InfoVal>
                <BiorecipeBlock>
                  <BiorecipeLine><Bold>Positive: </Bold>{card.biorecipe.positive}</BiorecipeLine>
                  <BiorecipeLine><Bold>Negative: </Bold>{card.biorecipe.negative}</BiorecipeLine>
                  <BiorecipeLine><Bold>Spontaneous: </Bold>{card.biorecipe.spontaneous}</BiorecipeLine>
                </BiorecipeBlock>
              </InfoVal>
            </tr>
            <tr>
              <InfoKey>DiSH</InfoKey>
              <DishVal><Bold>Update rule: </Bold>{card.dish}</DishVal>
            </tr>
            <tr>
              <InfoKey>Biological example(s)</InfoKey>
              <BioVal>{card.biological}</BioVal>
            </tr>
          </tbody>
        </InfoTable>
      )}

      <PlotSection>
        <PlotSectionLabel>Simulation plots</PlotSectionLabel>
        <PlotTableWrap $levels={levels.length}>
          <PlotTable>
            <thead>
              <tr>
                <ThCorner />
                {levels.map(l => <ThLevel key={l}>{l} levels</ThLevel>)}
              </tr>
            </thead>
            <tbody>
              {B_VALUES.map(b => (
                <tr key={b}>
                  <TdRow>B = {b}</TdRow>
                  {levels.map(l => (
                    <TdCell key={l}>
                      <PlotCell motifId={motif.id} level={l} b={b} />
                    </TdCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </PlotTable>
        </PlotTableWrap>
      </PlotSection>
    </CardEl>
  );
};

const CardLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  padding-left: 2px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RemoveBtn = styled.button`
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  border-radius: 3px;
  transition: background 0.1s, color 0.1s;

  &:hover { background: #DBEEFF; color: #003594; }
`;

const JustAddedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #003594;
  color: #FFB81C;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  animation: fadeSlideIn 0.2s ease-out, fadeOut 0.4s ease-in 1.4s forwards;

  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeOut {
    from { opacity: 1; }
    to   { opacity: 0; }
  }
`;

/* ─── Main export ────────────────────────────────────────── */
const ComputationalMotifViewer = ({ checkedKeys, configurations = [], onRemove, justAdded = false, newestId = null }) => {
  const allCards = useMemo(() => {
    const live = { id: "live", keys: checkedKeys, isLive: true };
    const saved = [...configurations].reverse().map(c => ({ ...c, isLive: false }));
    return [live, ...saved];
  }, [checkedKeys, configurations]);

  return (
    <Wrap>
      <CardsRow>
        {allCards.map((entry) => (
          <div key={entry.id} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <CardLabel>
              {entry.isLive
                ? <LiveBadge><LiveDot />Current selection</LiveBadge>
                : <span style={{ color: "#aaa" }}>Saved #{entry.id}</span>}
              {!entry.isLive && onRemove && (
                <RemoveBtn onClick={() => onRemove(entry.id)} aria-label="Remove">
                  <svg width="8" height="8" viewBox="0 0 10 10">
                    <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </RemoveBtn>
              )}
            </CardLabel>
            {entry.isLive && justAdded && (
              <JustAddedBanner>
                <svg width="13" height="13" viewBox="0 0 13 13">
                  <polyline points="2,7 5,10 11,3" fill="none" stroke="#FFB81C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Saved to comparison
              </JustAddedBanner>
            )}
            <InfoCard keys={entry.keys} isLive={entry.isLive} isNew={entry.id === newestId} />
          </div>
        ))}
      </CardsRow>
    </Wrap>
  );
};

export default ComputationalMotifViewer;